import { Component, Input, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HeroSettings,
  HeroContent,
  HeroLayoutVariant,
  CtaItem,
  SECTION_THEME,
} from './section-renderer.models';

/**
 * Hero Section Renderer
 * Full-width hero sections with multiple layout variants.
 * Based on Tailwind Plus hero examples.
 */
@Component({
  selector: 'dcp-hero-renderer',
  standalone: true,
  imports: [CommonModule],
  styles: [`
    .image-panel {
      width: 100%;
    }
    @media (min-width: 1024px) {
      .image-panel {
        width: var(--angle-split);
      }
    }
  `],
  template: `
    @switch (layoutVariant()) {
      @case ('angled-right') {
        <!-- Angled Right: Split layout with content left, image right -->
        <div class="relative isolate overflow-hidden" [style.background-color]="backgroundColor()">
          <div class="mx-auto max-w-7xl">
            <div class="relative z-10 pt-14 lg:w-full" [style.max-width.%]="100 - angleSplit() + 15">
              <!-- Angled divider SVG - positioned at the edge of image panel -->
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden="true"
                class="absolute inset-y-0 hidden h-full w-80 transform lg:block"
                [style.right]="'0'"
                [style.fill]="backgroundColor()"
              >
                <polygon points="0,0 90,0 50,100 0,100" />
              </svg>

              <div class="relative px-6 py-32 sm:py-40 lg:px-8 lg:py-56 lg:pr-0">
                <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl">
                  <!-- Announcement badge -->
                  @if (settings().showAnnouncement && content().announcementText) {
                    <div class="hidden sm:mb-10 sm:flex">
                      <div class="relative rounded-full px-3 py-1 text-sm/6 ring-1 ring-gray-900/10 hover:ring-gray-900/20 dark:ring-white/10 dark:hover:ring-white/20"
                           [style.color]="theme.textMutedColor">
                        {{ content().announcementText }}
                        @if (content().announcementLink) {
                          <a [href]="content().announcementLink"
                             class="whitespace-nowrap font-semibold ml-1"
                             [style.color]="theme.linkColor">
                            <span aria-hidden="true" class="absolute inset-0"></span>
                            Read more <span aria-hidden="true">&rarr;</span>
                          </a>
                        }
                      </div>
                    </div>
                  }

                  <!-- Title -->
                  <h1 class="text-pretty text-5xl font-semibold tracking-tight sm:text-7xl"
                      [style.color]="theme.textColor">
                    {{ content().title || 'Your Headline Here' }}
                  </h1>

                  <!-- Subtitle -->
                  @if (content().subtitle) {
                    <p class="mt-8 text-pretty text-lg font-medium sm:text-xl/8"
                       [style.color]="theme.textMutedColor">
                      {{ content().subtitle }}
                    </p>
                  }

                  <!-- CTA Buttons -->
                  @if (ctaItems().length > 0) {
                    <div class="mt-10 flex items-center gap-x-6">
                      @for (item of ctaItems(); track $index) {
                        @if (item.style === 'button') {
                          <a [href]="getCtaHref(item)"
                             class="px-3.5 py-2.5 text-sm font-semibold shadow-sm transition-colors"
                             [class.opacity-50]="item.isPlaceholder"
                             [class.border-dashed]="item.isPlaceholder"
                             [class.border-2]="item.isPlaceholder"
                             [style.background-color]="theme.primaryColor"
                             [style.color]="theme.buttonTextColor"
                             [style.border-radius]="buttonBorderRadius()">
                            {{ item.text }}
                          </a>
                        } @else {
                          <a [href]="getCtaHref(item)"
                             class="text-sm/6 font-semibold"
                             [class.opacity-50]="item.isPlaceholder"
                             [style.color]="theme.textColor">
                            {{ item.text }} <span aria-hidden="true">→</span>
                          </a>
                        }
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Right side image panel with background image -->
          <div
            class="lg:absolute lg:inset-y-0 lg:right-0 relative image-panel"
            [style.--angle-split.%]="angleSplit()"
          >
            @if (backgroundImageUrl()) {
              <img
                [src]="backgroundImageUrl()"
                alt=""
                class="aspect-[3/2] object-cover lg:aspect-auto lg:size-full"
              />
              <!-- Dark overlay for the image -->
              <div
                class="absolute inset-0 bg-black"
                [style.opacity]="backgroundOverlay()"
              ></div>
            } @else {
              <!-- Placeholder when no image -->
              <div class="aspect-[3/2] lg:aspect-auto lg:size-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                <svg class="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
            }
          </div>
        </div>
      }

      @case ('centered-background') {
        <!-- Centered with Background Image -->
        <div
          class="relative isolate overflow-hidden"
          [style.background-color]="backgroundImageUrl() ? 'transparent' : backgroundColor()"
        >
          <!-- Background image -->
          @if (backgroundImageUrl()) {
            <img
              [src]="backgroundImageUrl()"
              alt=""
              class="absolute inset-0 size-full object-cover z-0"
            />
            <!-- Dark overlay for text readability -->
            <div
              class="absolute inset-0 bg-black z-0"
              [style.opacity]="backgroundOverlay()"
            ></div>
          }

          <!-- Gradient blur effects -->
          <div aria-hidden="true" class="absolute inset-x-0 -top-40 z-0 transform-gpu overflow-hidden blur-3xl sm:-top-80">
            <div
              style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
              class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
              [style.background]="'linear-gradient(to top right, ' + theme.primaryColor + ', ' + theme.accentColor + ')'"
            ></div>
          </div>

          <div class="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
            <div class="mx-auto max-w-2xl py-32 sm:py-48 lg:py-56">
              <!-- Announcement badge -->
              @if (settings().showAnnouncement && content().announcementText) {
                <div class="hidden sm:mb-8 sm:flex sm:justify-center">
                  <div class="relative rounded-full px-3 py-1 text-sm/6 ring-1"
                       [class.ring-white/20]="backgroundImageUrl()"
                       [class.ring-gray-900/10]="!backgroundImageUrl()"
                       [style.color]="backgroundImageUrl() ? 'rgba(255,255,255,0.8)' : theme.textMutedColor">
                    {{ content().announcementText }}
                    @if (content().announcementLink) {
                      <a [href]="content().announcementLink"
                         class="font-semibold ml-1"
                         [style.color]="backgroundImageUrl() ? '#ffffff' : theme.linkColor">
                        <span aria-hidden="true" class="absolute inset-0"></span>
                        Read more <span aria-hidden="true">&rarr;</span>
                      </a>
                    }
                  </div>
                </div>
              }

              <div class="text-center">
                <!-- Title -->
                <h1 class="text-balance text-5xl font-semibold tracking-tight sm:text-7xl"
                    [style.color]="backgroundImageUrl() ? '#ffffff' : theme.textColor">
                  {{ content().title || 'Your Headline Here' }}
                </h1>

                <!-- Subtitle -->
                @if (content().subtitle) {
                  <p class="mt-8 text-pretty text-lg font-medium sm:text-xl/8"
                     [style.color]="backgroundImageUrl() ? 'rgba(255,255,255,0.8)' : theme.textMutedColor">
                    {{ content().subtitle }}
                  </p>
                }

                <!-- CTA Buttons -->
                @if (ctaItems().length > 0) {
                  <div class="mt-10 flex items-center justify-center gap-x-6">
                    @for (item of ctaItems(); track $index) {
                      @if (item.style === 'button') {
                        <a [href]="getCtaHref(item)"
                           class="px-3.5 py-2.5 text-sm font-semibold shadow-sm transition-colors"
                           [class.opacity-50]="item.isPlaceholder"
                           [class.border-dashed]="item.isPlaceholder"
                           [class.border-2]="item.isPlaceholder"
                           [style.background-color]="theme.primaryColor"
                           [style.color]="theme.buttonTextColor"
                           [style.border-radius]="buttonBorderRadius()">
                          {{ item.text }}
                        </a>
                      } @else {
                        <a [href]="getCtaHref(item)"
                           class="text-sm/6 font-semibold"
                           [class.opacity-50]="item.isPlaceholder"
                           [style.color]="backgroundImageUrl() ? '#ffffff' : theme.textColor">
                          {{ item.text }} <span aria-hidden="true">→</span>
                        </a>
                      }
                    }
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Bottom gradient blur -->
          <div aria-hidden="true" class="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
            <div
              style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
              class="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
              [style.background]="'linear-gradient(to top right, ' + theme.primaryColor + ', ' + theme.accentColor + ')'"
            ></div>
          </div>
        </div>
      }

      @default {
        <!-- Simple: Basic centered hero -->
        <div
          class="relative py-24 sm:py-32 isolate overflow-hidden"
          [style.background-color]="backgroundImageUrl() ? 'transparent' : backgroundColor()"
        >
          <!-- Background image for simple variant -->
          @if (backgroundImageUrl()) {
            <img
              [src]="backgroundImageUrl()"
              alt=""
              class="absolute inset-0 size-full object-cover z-0"
            />
            <!-- Dark overlay for text readability -->
            <div
              class="absolute inset-0 bg-black z-0"
              [style.opacity]="backgroundOverlay()"
            ></div>
          }
          <div class="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
            <div class="mx-auto max-w-2xl lg:mx-0">
              <!-- Eyebrow text -->
              @if (settings().showAnnouncement && content().announcementText) {
                <p class="text-base/7 font-semibold" [style.color]="backgroundImageUrl() ? '#ffffff' : theme.primaryColor">
                  {{ content().announcementText }}
                </p>
              }

              <!-- Title -->
              <h1 class="mt-2 text-5xl font-semibold tracking-tight sm:text-7xl"
                  [style.color]="backgroundImageUrl() ? '#ffffff' : theme.textColor">
                {{ content().title || 'Your Headline Here' }}
              </h1>

              <!-- Subtitle -->
              @if (content().subtitle) {
                <p class="mt-8 text-pretty text-lg font-medium sm:text-xl/8"
                   [style.color]="backgroundImageUrl() ? 'rgba(255,255,255,0.8)' : theme.textMutedColor">
                  {{ content().subtitle }}
                </p>
              }

              <!-- CTA Buttons -->
              @if (ctaItems().length > 0) {
                <div class="mt-10 flex items-center gap-x-6">
                  @for (item of ctaItems(); track $index) {
                    @if (item.style === 'button') {
                      <a [href]="getCtaHref(item)"
                         class="px-3.5 py-2.5 text-sm font-semibold shadow-sm transition-colors"
                         [class.opacity-50]="item.isPlaceholder"
                         [class.border-dashed]="item.isPlaceholder"
                         [class.border-2]="item.isPlaceholder"
                         [style.background-color]="theme.primaryColor"
                         [style.color]="theme.buttonTextColor"
                         [style.border-radius]="buttonBorderRadius()">
                        {{ item.text }}
                      </a>
                    } @else {
                      <a [href]="getCtaHref(item)"
                         class="text-sm/6 font-semibold"
                         [class.opacity-50]="item.isPlaceholder"
                         [style.color]="backgroundImageUrl() ? '#ffffff' : theme.textColor">
                        {{ item.text }} <span aria-hidden="true">→</span>
                      </a>
                    }
                  }
                </div>
              }
            </div>
          </div>
        </div>
      }
    }
  `,
})
export class HeroRendererComponent {
  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  protected get theme() { return this.themeSignal(); }

  /** Section settings */
  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(value as HeroSettings);
  }
  private _settings = signal<HeroSettings>({});
  readonly settings = this._settings.asReadonly();

  /** Section content */
  @Input() set contentInput(value: Record<string, unknown>) {
    this._content.set(value as HeroContent);
  }
  private _content = signal<HeroContent>({});
  readonly content = this._content.asReadonly();

  /** Current locale for language-specific content */
  @Input() set locale(value: 'de' | 'en') {
    this._locale.set(value);
  }
  private _locale = signal<'de' | 'en'>('de');

  /** Computed layout variant with fallback */
  readonly layoutVariant = computed<HeroLayoutVariant>(() => {
    return (this._settings().layoutVariant as HeroLayoutVariant) || 'simple';
  });

  /** Computed background color */
  readonly backgroundColor = computed(() => {
    const settings = this._settings();
    if (settings.backgroundType === 'solid' && settings.backgroundColor) {
      return settings.backgroundColor;
    }
    return this.themeSignal()?.backgroundColor || '#ffffff';
  });

  /** Computed background image URL (from settings or content) */
  readonly backgroundImageUrl = computed(() => {
    const settings = this._settings();
    const content = this._content();

    // Use settings.backgroundImageUrl if available (resolved from backgroundImageId by page-editor)
    if (settings.backgroundImageUrl) {
      return settings.backgroundImageUrl;
    }

    // Check if backgroundImageId is actually a full URL (from public API)
    if (settings.backgroundImageId && typeof settings.backgroundImageId === 'string') {
      // If it's a URL (starts with http), use it directly
      if (settings.backgroundImageId.startsWith('http')) {
        return settings.backgroundImageId;
      }
    }

    // Fall back to content.imageUrl for backward compatibility (legacy data)
    return content.imageUrl || null;
  });

  /** Computed background overlay opacity */
  readonly backgroundOverlay = computed(() => {
    const settings = this._settings();
    // Default to 0.7 (70%) overlay for readability
    return settings.backgroundOverlay ?? 0.7;
  });

  /** Computed angle split percentage for angled-right variant */
  readonly angleSplit = computed(() => {
    const settings = this._settings();
    // Default to 50% split
    return settings.angleSplit ?? 50;
  });

  /** Computed button border-radius CSS value from theme */
  readonly buttonBorderRadius = computed(() => {
    const radius = this.themeSignal()?.buttonRadius || 'md';
    const radiusMap: Record<string, string> = {
      none: '0',
      sm: '0.125rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      full: '9999px',
    };
    return radiusMap[radius] || '0.375rem';
  });

  /**
   * Computed CTA items - gets from settings (new format) or content (legacy format).
   * Returns items with text resolved for current locale.
   * Filters out disabled items. Shows placeholder text if current language is missing.
   */
  readonly ctaItems = computed<CtaItem[]>(() => {
    const settings = this._settings();
    const content = this._content();
    const locale = this._locale();

    // Check for new ctaItems in settings (shared config with textDe/textEn)
    if (settings.ctaItems && settings.ctaItems.length > 0) {
      return settings.ctaItems
        .filter(item => item.enabled !== false) // Only include enabled items
        .map((item, index) => {
          const currentLangText = locale === 'de' ? item.textDe : item.textEn;
          const otherLangText = locale === 'de' ? item.textEn : item.textDe;

          // Use current language text, fallback to other language, then placeholder
          let text = currentLangText;
          let isPlaceholder = false;

          if (!text || text.trim().length === 0) {
            if (otherLangText && otherLangText.trim().length > 0) {
              text = otherLangText;
              isPlaceholder = true;
            } else {
              // Default placeholder based on style and index
              text = item.style === 'button'
                ? (locale === 'de' ? 'Button-Text' : 'Button text')
                : (locale === 'de' ? 'Link-Text' : 'Link text');
              isPlaceholder = true;
            }
          }

          return {
            ...item,
            text,
            isPlaceholder,
          };
        });
    }

    // Check for ctaItems in content (legacy new format during migration)
    if (content.ctaItems && content.ctaItems.length > 0) {
      return content.ctaItems
        .map(item => ({
          ...item,
          text: item.text || (locale === 'de' ? item.textDe : item.textEn),
        }))
        .filter(item => item.text && item.text.trim().length > 0);
    }

    // Fall back to legacy ctaText/ctaUrl format in content
    const items: CtaItem[] = [];

    if (content.ctaText) {
      items.push({
        style: 'button',
        linkType: 'external',
        url: content.ctaUrl || '#',
        text: content.ctaText,
      });
    }

    if (content.secondaryCtaText) {
      items.push({
        style: 'link',
        linkType: 'external',
        url: content.secondaryCtaUrl || '#',
        text: content.secondaryCtaText,
      });
    }

    return items;
  });

  /** Get the URL or scroll target for a CTA item */
  getCtaHref(item: CtaItem): string {
    if (item.linkType === 'scroll' && item.targetSectionAnchor) {
      return `#section-${item.targetSectionAnchor}`;
    }
    return item.url || '#';
  }
}
